import {CopyOutlined, DeleteFilled, PlusOutlined, ReloadOutlined} from "@ant-design/icons";
import {Breadcrumb, Button, Divider, message, Modal, Space, Table} from "antd";
import _ from "lodash";
import React, {useContext, useEffect, useMemo, useState} from "react";
import {Link, useLocation, useNavigate} from "react-router-dom";
import {AppContextContext, VulReportServiceContext} from "../../Contexts";
import {useTableHandler} from "../../sal-ui/TableHandler";
import PagedResult from "../../service/PagedResult";
import {DocumentTitle} from "../DocumentTitle";

import * as styles from "./VulReportList.module.css";
import * as globalStyles from "../App.module.css";
import {ColumnType} from "antd/es/table";
import {formatDateTime} from "../../utils/FormatUtils";
import VulReport from "../../domain/VulReport";
import {Permission} from "../../domain/auth/Permission";
import {renderOrganizationLink} from "../common/OrganizationLink";
import VulReportFilter, {createDefaultVulReportFilterValues} from "./VulReportFilter";
import {FormPersister} from "../../sal-ui/FormPersister";
import {ascendingFirstSortDirections, descendingFirstSortDirections} from "../../sal-ui/TableSortDirections";

const PERSISTENT_IDENT = "VulReportList";
const FILTER_DEBOUNCE_MS = 500;

type ReportItemCount = {
    newItems: number;
    modifiedItems: number;
};

function VulReportList() {
    const appContext = useContext(AppContextContext);
    const vulReportService = useContext(VulReportServiceContext);
    const location = useLocation();
    const navigate = useNavigate();
    const tableHandler = useTableHandler("timestampTo desc", {
        reloadFunction: reload,
        persistentIdent: PERSISTENT_IDENT
    });
    const filterFormPersister = useMemo(() => new FormPersister(PERSISTENT_IDENT), []);
    const debouncedOnFinishFilter = useMemo(() => _.debounce(onFinishFilter, FILTER_DEBOUNCE_MS), []);
    const [reports, setReports] = useState<PagedResult<VulReport>>();
    const [itemCount, setItemCount] = useState<Record<string, ReportItemCount>>({});
    const [filterValues, setFilterValues] = useState<any>();

    /* eslint-disable react-hooks/exhaustive-deps */
    useEffect(() => {
        const searchParams = new URLSearchParams(location.search);
        const loadedValues = searchParams.has("filter")
            ? filterFormPersister.loadValues(FormPersister.decodeUrlState(searchParams.get("filter")!))
            : filterFormPersister.loadValues();

        const defaultValues = createDefaultVulReportFilterValues();
        const values = _.isEmpty(loadedValues)
            ? defaultValues
            : {...defaultValues, ...loadedValues};

        setFilterValues(values);

        if (searchParams.has("filter")) {
            navigate(location.pathname, {replace: true});
        }
    }, []);

    useEffect(() => {
        if (filterValues !== undefined) {
            debouncedOnFinishFilter(filterValues);
        }
    }, [filterValues]);

    const title = "Vulnerability reports";

    const columns: ColumnType<VulReport>[] = [
        {
            dataIndex: "name",
            title: "Name",
            sorter: true,
            sortDirections: ascendingFirstSortDirections,
            render: renderName
        },
        {
            dataIndex: "timestampFrom",
            title: "Start date",
            width: 160,
            sorter: true,
            sortDirections: descendingFirstSortDirections,
            render: formatDateTime
        },
        {
            dataIndex: "timestampTo",
            title: "End date",
            width: 160,
            sorter: true,
            sortDirections: descendingFirstSortDirections,
            defaultSortOrder: "descend",
            render: formatDateTime
        },
        {
            dataIndex: ["definition", "name"],
            title: "Report definition",
            sorter: true,
            sortDirections: ascendingFirstSortDirections,
            align: "left",
            render: renderDefinition
        },
        {
            dataIndex: "vulViews",
            title: "Vulnerability views",
            align: "left",
            render: renderVulViews
        },
        {
            title: "Items (new / modified)",
            align: "center",
            render: renderItemCount
        }
    ];

    if (appContext.user.hasPermission(Permission.REPORT__MANAGE)) {
        columns.push(
            {
                title: "Actions",
                width: 100,
                className: globalStyles["table-actions"],
                render: renderAction
            }
        );
    }

    if (appContext.user.isSystemAdmin()) {
        columns.splice(3, 0, {
                dataIndex: ["organization", "name"],
                title: "Organization",
                sorter: true,
                sortDirections: ascendingFirstSortDirections,
                align: "left",
                render: renderOrganization
            },
        );
    }

    return (
        <DocumentTitle title={title}>
            <>
                <Breadcrumb className={globalStyles["common__breadcrumb"]}>
                    <Breadcrumb.Item>{appContext.config?.appName}</Breadcrumb.Item>
                    <Breadcrumb.Item>{title}</Breadcrumb.Item>
                </Breadcrumb>

                <h1>{title}</h1>

                <VulReportFilter
                    className={styles.filter}
                    showOrganization={appContext.user.isSystemAdmin()}
                    values={filterValues}
                    persistentStateIdent={PERSISTENT_IDENT}
                    onChange={setFilterValues}
                />

                <div className={globalStyles["common__top-button-bar"]}>
                    <Button className={"btn-seamless"} icon={<ReloadOutlined/>} onClick={tableHandler.reload}/>

                    {appContext.user.hasPermission(Permission.REPORT__MANAGE) &&
                        <Button type={"primary"}
                                icon={<PlusOutlined/>}
                                onClick={() => navigate("/vul-reports/add")}>Add</Button>
                    }
                </div>

                <Table className={styles.table}
                       showSorterTooltip={false}
                       dataSource={reports?.data}
                       size="middle"
                       loading={tableHandler.loading}
                       onChange={tableHandler.onTableChange}
                       pagination={tableHandler.pagination}
                       onRow={onRow}
                       rowKey="id"
                       columns={columns}
                />
            </>
        </DocumentTitle>
    );

    function renderDefinition(value: any, report: VulReport) {
        if (report.definition) {
            return <Link to={`/definitions/vul-reports/${report.definition.id}`}>{report.definition.name}</Link>;
        } else {
            return <i>manual report</i>;
        }
    }

    function renderOrganization(value: string | undefined, report: VulReport) {
        return renderOrganizationLink(report.organization, value);
    }

    function renderVulViews(value: any, report: VulReport) {
        return (
            <Space wrap={true} split={<Divider type="vertical"/>}>
                {report.vulViews?.map(view => (
                    <Link key={view.id} to={`/vul-source-items/views/${view.id}`}>{view.name}</Link>
                ))}
            </Space>
        );
    }

    function renderItemCount(_value: unknown, report: VulReport) {
        return (
            <>{itemCount[report.id!]?.newItems} / {itemCount[report.id!]?.modifiedItems}</>
        );
    }

    function renderAction(_value: unknown, report: VulReport) {
        return (
            <Space>
                <Button icon={<CopyOutlined/>} className={"ant-btn-icon-only"}
                        title={"Clone"}
                        onClick={() => navigate(`/vul-reports/clone/${report.id}`)}/>

                <Button icon={<DeleteFilled/>} className={"ant-btn-icon-only"}
                        title={"Delete"}
                        onClick={() => {
                            Modal.confirm({
                                content: "Do you really want to delete this report?",
                                okText: "Delete",
                                cancelText: "Cancel",
                                onOk: () => onDeleteConfirm(report)
                            });
                        }}/>
            </Space>
        );
    }

    function onDeleteConfirm(report: VulReport) {
        vulReportService.delete(report)
            .then(() => {
                message.success("Vulnerability report deleted.");

                tableHandler.reload();
            });
    }

    function onRow(report: VulReport) {
        return {onDoubleClick: () => navigate(`/vul-reports/${report.id}`)};
    }

    function renderName(value: string, report: VulReport) {
        return <Link to={`/vul-reports/${report.id}`}>{value}</Link>;
    }

    function onFinishFilter(values: any) {
        const rqbQuery = createRqbQuery(values);

        filterFormPersister.saveState(values);

        tableHandler.onRqbSearchSubmit(rqbQuery);
    }

    function createRqbQuery(values: any) {
        const rqbQuery = {
            combinator: "and",
            rules: [] as any[]
        };

        if (appContext.user.isSystemAdmin() && values.organizationIds?.length > 0) {
            rqbQuery.rules.push({
                field: "organization.id",
                operator: "in",
                value: values.organizationIds
            });
        }

        if (values.definitionIds?.length > 0) {
            rqbQuery.rules.push({
                field: "definition.id",
                operator: "in",
                value: values.definitionIds
            });
        }

        return rqbQuery;
    }

    async function reload() {
        const value = await vulReportService.getList(tableHandler.queryOptions);

        tableHandler.updateTotal(value.total);

        setReports(value);
        setItemCount({});

        const reportItemCounts = await Promise.all(value.data.map(report =>
            vulReportService.countItems(report).then(count => [report.id!, count] as const)
        ));

        setItemCount(Object.fromEntries(reportItemCounts));
    }
}

export default VulReportList;
